import { useQuery } from '@apollo/client';
import { STUDIOS_FOR_SERVICE_SELECTION, GET_STUDIO_MINIMAL } from '../graphql/queries/queries';
import { useMemo } from 'react';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import awsConfig from '../config/aws-config';
import { buildCoarseLocationText } from '../util/locationPrivacy';

export const useFetchStudiosForService = (userId) => {
  const { data, loading, error } = useQuery(STUDIOS_FOR_SERVICE_SELECTION, {
    variables: { 
      userID: userId,
      limit: 100
    },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  // Process studios data - get first image and format location
  const studios = useMemo(() => {
    if (!data?.studiosByUserID?.items) return [];

    return data.studiosByUserID.items.map(studio => {
      const processedStudio = { ...studio };

      // Get first image and process it
      if (processedStudio.images && Array.isArray(processedStudio.images) && processedStudio.images.length > 0) {
        const firstImageKey = processedStudio.images[0];
        if (firstImageKey) {
          try {
            processedStudio.image = getCloudFrontImageUrl(firstImageKey, {
              width: 400,
              height: 300,
              fit: 'cover'
            });
            processedStudio.imageKey = firstImageKey;
          } catch (error) {
            try {
              const cleanKey = firstImageKey.startsWith('public/') ? firstImageKey : `public/${firstImageKey}`;
              processedStudio.image = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
              processedStudio.imageKey = firstImageKey;
            } catch (fallbackError) {
              processedStudio.image = '';
              processedStudio.imageKey = null;
            }
          }
        } else {
          processedStudio.image = '';
          processedStudio.imageKey = null;
        }
      } else {
        processedStudio.image = '';
        processedStudio.imageKey = null;
      }

      processedStudio.displayLocation = buildCoarseLocationText(processedStudio);

      return processedStudio;
    });
  }, [data]);

  return {
    studios,
    loading,
    error
  };
};

export const useFetchStudioById = (studioId) => {
  const { data, loading, error } = useQuery(GET_STUDIO_MINIMAL, {
    variables: { 
      id: studioId
    },
    skip: !studioId,
    fetchPolicy: 'cache-and-network',
  });

  // Process studio data - get first image, studio name, and location
  const studio = useMemo(() => {
    if (!data?.getStudio) return null;

    const processedStudio = { ...data.getStudio };

    // Get first image and process it
    if (processedStudio.images && Array.isArray(processedStudio.images) && processedStudio.images.length > 0) {
      const firstImageKey = processedStudio.images[0];
      if (firstImageKey) {
        try {
          processedStudio.image = getCloudFrontImageUrl(firstImageKey, {
            width: 400,
            height: 300,
            fit: 'cover'
          });
          processedStudio.imageKey = firstImageKey;
        } catch (error) {
          try {
            const cleanKey = firstImageKey.startsWith('public/') ? firstImageKey : `public/${firstImageKey}`;
            processedStudio.image = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
            processedStudio.imageKey = firstImageKey;
          } catch (fallbackError) {
            processedStudio.image = '';
            processedStudio.imageKey = null;
          }
        }
      } else {
        processedStudio.image = '';
        processedStudio.imageKey = null;
      }
    } else {
      processedStudio.image = '';
      processedStudio.imageKey = null;
    }

    processedStudio.displayLocation = buildCoarseLocationText(processedStudio);

    return processedStudio;
  }, [data]);

  return {
    studio,
    loading,
    error
  };
};
